import { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa6';
import { FiShield } from 'react-icons/fi';
import { IoIosRefresh } from 'react-icons/io';
import { toast } from 'react-toastify';
import ThreeColumnLayout from '../../layouts/ThreeColumnLayout';
import FilterSidebar from '../../organisms/FilterSidebar';
import FilterSkeleton from '../../atoms/FilterSkeleton';
import ATable from '../../organisms/ATable';
import TableSkeleton from '../../atoms/TableSkeleton';
import Loading from '../../ions/Loading';
import PageFooter from '../../molecules/PageFooter';
import TextEdit from '../../molecules/TextEdit';
import { collateralApi, ApiError } from '../../../services/collateralApi';
import NewAnalysisFromOperation from './NewAnalysisFromOperation';
import {
    formatCurrency,
    formatPercentage,
    formatCpfCnpj,
    getGuaranteeStatusColor,
    getIntensityColor,
    getTraderStatusColor,
    getPageListValidationColor,
    type GuaranteeStatus,
    type AnalysisIntensity,
} from './collateralUtils';

type FilterKey = 'operationStatus' | 'guaranteeStatus' | 'traderStatus' | 'validationStatus' | 'intensity' | 'operationType';

const FILTER_SECTIONS: { key: FilterKey; title: string }[] = [
    { key: 'operationStatus', title: 'STATUS OPERAÇÃO' },
    { key: 'guaranteeStatus', title: 'STATUS GARANTIA' },
    { key: 'traderStatus', title: 'STATUS TRADER' },
    { key: 'validationStatus', title: 'STATUS VALIDAÇÃO' },
    { key: 'intensity', title: 'INTENSIDADE' },
    { key: 'operationType', title: 'TIPO OPERAÇÃO' },
];

const GuaranteePageList = () => {
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [showNewForm, setShowNewForm] = useState(false);
    const [filters, setFilters] = useState<Record<FilterKey, string>>({
        operationStatus: '', guaranteeStatus: '', traderStatus: '',
        validationStatus: '', intensity: '', operationType: '',
    });
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState('operationCode');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
    const [rows, setRows] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [apiInfo, setApiInfo] = useState({ version: '', environment: '' });
    const [filterCounts, setFilterCounts] = useState<Record<string, Record<string, number>>>({});
    const pageSize = 50;

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const result = await collateralApi.getGuarantees({
                page, pageSize,
                operationStatus: filters.operationStatus || undefined,
                guaranteeStatus: filters.guaranteeStatus || undefined,
                traderStatus: filters.traderStatus || undefined,
                validationStatus: filters.validationStatus || undefined,
                intensity: filters.intensity || undefined,
                operationType: filters.operationType || undefined,
                search: search || undefined,
                sortField: sortField || undefined,
                sortOrder: sortOrder || undefined,
            });
            setRows(result.items || []);
            setTotal(result.total || 0);
            setApiInfo({ version: result.apiVersion || '', environment: result.environment || '' });
            setFilterCounts(result.filterCounts || {});
        } catch (e: any) {
            if (e instanceof ApiError) {
                if (e.status === 403) return navigate('/access-denied');
                if (e.status === 404) return navigate('/not-found');
            }
            navigate('/error');
        } finally {
            setIsLoading(false);
            setInitialLoading(false);
        }
    }, [page, filters, search, sortField, sortOrder]);

    useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

    const setFilter = (key: FilterKey, value: string) => {
        setFilters(prev => ({ ...prev, [key]: prev[key] === value ? '' : value }));
        setPage(1);
    };

    const clearFilters = () => {
        setFilters({ operationStatus: '', guaranteeStatus: '', traderStatus: '', validationStatus: '', intensity: '', operationType: '' });
        setPage(1);
    };

    const hasActiveFilter = Object.values(filters).some(v => v !== '');

    const handleRowClick = (row: any) => {
        navigate(`/collateral/guarantees/${row.id}/view`);
    };

    const columns = [
        { id: 'op', title: 'Operação', field: 'operationCode', canSort: true, width: '100px', cssClass: 'sticky-left z-20' },
        { id: 'assignor', title: 'Cedente', field: 'assignor', canSort: true, width: '300px', cssClass: 'sticky-left-1 z-20' },
        { id: 'risk', title: 'Risco', field: 'assignorRisk', canSort: true, width: '150px', cssClass: 'text-right' },
        { id: 'riskPct', title: '% Risco', field: 'assignorRiskPercentage', canSort: true, width: '80px', cssClass: 'text-right' },
        { id: 'debtors', title: 'Principais Sacados', field: 'mainDebtors', canSort: false, width: '180px' },
        { id: 'contract', title: 'Nº Contrato', field: 'contractType', canSort: true, width: '120px' },
        { id: 'issueDate', title: 'Emissão', field: 'issueDate', canSort: true, width: '100px', cssClass: 'text-center' },
        { id: 'obligation', title: 'Obrigação', field: 'obligation', canSort: false, width: '130px', cssClass: 'text-center' },
        { id: 'intensity', title: 'Intensidade', field: 'analysisIntensity', canSort: false, width: '110px', cssClass: 'text-center' },
        { id: 'status', title: 'Status', field: 'guaranteeStatus', canSort: false, width: '130px', cssClass: 'text-center' },
        { id: 'trader', title: 'Status Trader', field: 'traderStatus', canSort: false, width: '140px', cssClass: 'text-center' },
        { id: 'validation', title: 'Validação', field: 'validationStatus', canSort: false, width: '140px', cssClass: 'text-center' },
    ];

    const filterGroups = initialLoading ? [] : [
        {
            title: '',
            children: (
                <div className="space-y-4">
                    <button onClick={clearFilters}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${!hasActiveFilter
                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                        <div className="flex items-center justify-between">
                            <span>Todos</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                {Object.values(filterCounts.operationStatus || {}).reduce((a: number, d: any) => a + (d.count || 0), 0)}
                            </span>
                        </div>
                    </button>

                    {FILTER_SECTIONS.map(section => {
                        const counts = filterCounts[section.key] || {};
                        if (Object.keys(counts).length === 0) return null;
                        return (
                            <div key={section.key} className="border-t border-gray-200 dark:border-slate-700 pt-3">
                                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-3">{section.title}</div>
                                <div className="space-y-1">
                                    {Object.entries(counts).sort(([a], [b]) => a.localeCompare(b)).map(([key, data]: [string, any]) => (
                                        <button key={key} onClick={() => setFilter(section.key, key)}
                                            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${filters[section.key] === key
                                                ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                                            <div className="flex items-center justify-between">
                                                <span className="truncate">{data.label || key}</span>
                                                <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">{data.count || data}</span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ),
        },
    ];

    return (
        <>
            {isLoading && <Loading />}

            {!showNewForm && (
                <button onClick={() => setShowNewForm(true)}
                    className="fixed top-[69px] right-[10px] w-10 h-10 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 z-50 flex items-center justify-center border-2 border-white dark:border-slate-800 bg-blue-600 hover:bg-blue-700 text-white"
                    title="Nova Análise de Garantia">
                    <FaPlus size={14} />
                </button>
            )}

            <ThreeColumnLayout
                leftSidebarTitle={
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center space-x-2">
                            <FiShield className="text-blue-600" size={18} />
                            <span>Garantias</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            {apiInfo.version && <div>v{apiInfo.version}</div>}
                            <div className={`font-medium ${apiInfo.environment === 'prd' ? 'text-red-600' :
                                apiInfo.environment === 'hml' ? 'text-yellow-600' : 'text-green-600'}`}>
                                {(apiInfo.environment || '').toUpperCase()}
                            </div>
                        </div>
                    </div>
                }
                leftSidebar={
                    <FilterSidebar filterGroups={filterGroups}>
                        {initialLoading && <FilterSkeleton />}
                    </FilterSidebar>
                }
                showRightPanel={showNewForm}
                onCloseRightPanel={() => setShowNewForm(false)}
                rightPanel={showNewForm ? (
                    <div className="h-full p-6">
                        <NewAnalysisFromOperation onClose={() => setShowNewForm(false)} onCreated={() => { setShowNewForm(false); setRefreshKey(k => k + 1); toast.success('Análise criada'); }} />
                    </div>
                ) : null}
            >
                <div className="flex flex-col h-full bg-white dark:bg-slate-800 relative overflow-hidden">
                    {/* Header */}
                    <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-800 z-10">
                        <div className="flex items-center justify-between">
                            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Análise de Garantias</h1>
                            <div className="flex items-center space-x-2">
                                <button onClick={() => setRefreshKey(k => k + 1)}
                                    className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                    title="Atualizar lista">
                                    <IoIosRefresh size={18} />
                                </button>
                                <div className="w-80">
                                    <TextEdit label="Buscar operação, cedente, CNPJ, sacado..." value={searchInput}
                                        onChange={e => setSearchInput(e.target.value)}
                                        onKeyDown={(e: any) => { if (e.key === 'Enter') { setSearch(searchInput); setPage(1); } }} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="flex-1 overflow-x-auto overflow-y-auto [&_table]:table-fixed">
                        {initialLoading ? (
                            <TableSkeleton rows={12} />
                        ) : (
                            <ATable headers={columns} fieldSorted={sortField} orderSorted={sortOrder}
                                onSort={(f: string, o: 'ASC' | 'DESC') => { setSortField(f); setSortOrder(o); }}>
                                {rows.length === 0 ? (
                                    <tr><td colSpan={columns.length} className="py-8 text-center text-gray-500 dark:text-gray-400">Nenhuma análise encontrada</td></tr>
                                ) : rows.map((row: any, index: number) => (
                                    <tr key={row.id}
                                        className={`group hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer ${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'}`}
                                        onClick={() => handleRowClick(row)}>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm font-medium dark:text-white sticky-left max-w-[100px]" title={row.operationCode}>
                                            <div className="truncate">{row.operationCode}</div>
                                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-700 dark:bg-slate-600 dark:text-slate-200">{row.operationType}</span>
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white sticky-left-1 max-w-[300px]" title={row.assignor}>
                                            <div className="truncate">{row.assignor}</div>
                                            <div className="text-xs text-gray-400 dark:text-gray-500">{formatCpfCnpj(row.assignorTaxId)}</div>
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-right text-sm dark:text-white">{formatCurrency(row.assignorRisk || 0)}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-right text-sm dark:text-white">{formatPercentage(row.assignorRiskPercentage || 0)}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-gray-300 truncate" title={row.mainDebtors}>{row.mainDebtors}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-gray-300">{row.contractType}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center text-sm dark:text-gray-300">{row.issueDate}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center text-xs font-medium dark:text-gray-300 uppercase">{row.obligation}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center">
                                            {row.analysisIntensity && row.analysisIntensity !== '—'
                                                ? <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getIntensityColor(row.analysisIntensity as AnalysisIntensity)}`}>{row.analysisIntensity}</span>
                                                : <span className="text-xs text-gray-400">{row.analysisIntensity}</span>}
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center">
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getGuaranteeStatusColor(row.guaranteeStatus)}`}>{row.guaranteeStatus}</span>
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center">
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getTraderStatusColor(row.traderStatus)}`}>{row.traderStatus}</span>
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center">
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPageListValidationColor(row.validationStatus)}`}>{row.validationStatus}</span>
                                        </td>
                                    </tr>
                                ))}
                            </ATable>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex-shrink-0 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                        <PageFooter currentPage={page - 1} totalPages={Math.ceil(total / pageSize)} itemsPerPage={pageSize} totalElements={total} onPageChange={(p) => setPage(p + 1)} />
                    </div>
                </div>
            </ThreeColumnLayout>

            <Outlet context={{ onDataChange: () => setRefreshKey(k => k + 1) }} />
        </>
    );
};

export default GuaranteePageList;
